'use client'

import { useState, Fragment } from 'react'

interface EmailCaptureModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  title?: string
  description?: string
}

export default function EmailCaptureModal({
  isOpen,
  onClose,
  onSuccess,
  title = "Get More Tools & Updates",
  description = "Join our newsletter to receive updates on new tools, features, and exclusive content directly to your inbox."
}: EmailCaptureModalProps) {
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError('')

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000))

    if (email && email.includes('@')) { // Basic validation
      localStorage.setItem('hasSubmittedEmail', 'true')
      setSubmitted(true)
      onSuccess()
      setTimeout(() => {
        onClose();
        // Reset for next time modal might open, though unlikely in same session after success
        setSubmitted(false); 
        setEmail('');
      }, 2000);
    } else {
      setError('Please enter a valid email address.')
    }
    setIsSubmitting(false)
  }

  if (!isOpen) {
    return null
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-neutral-800 p-6 sm:p-8 rounded-xl shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl sm:text-2xl font-semibold text-neutral-800 dark:text-neutral-100">
            {submitted ? "Thanks for Subscribing!" : title}
          </h2>
          <button
            onClick={onClose}
            className="text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 transition-colors"
            aria-label="Close modal"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {submitted ? (
          <p className="text-center text-neutral-700 dark:text-neutral-300 py-4">
            You'll be the first to know about new tools and updates.
          </p>
        ) : (
          <>
            <p className="text-neutral-600 dark:text-neutral-300 mb-6 text-sm sm:text-base">
              {description}
            </p>
            <form onSubmit={handleSubmit}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full p-3 border border-neutral-300 dark:border-neutral-600 rounded-md mb-4 focus:ring-2 focus:ring-blue-500 outline-none bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 dark:placeholder-neutral-500"
                required
              />
              {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-md transition-colors disabled:opacity-70"
              >
                {isSubmitting ? 'Submitting...' : 'Subscribe'}
              </button>
            </form>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-4 text-center">
              We respect your privacy. No spam.
            </p>
          </>
        )}
      </div>
    </div>
  )
} 